import React, {useMemo} from "react";
import classnames from "classnames";
import {AccountingTransactionTypes} from "../../../../../core/constants/enums";
import Utils, {createUUId, dateComparator, formatMoney, formatPhoneNumber} from "../../../../../core/services/utils/utils";
import DateUtils from "../../../../../core/services/date-utils";
import ElementIds from "../../../../../core/models/element-ids";
import EnvService from "../../../../../core/services/env";

const itemHasPrintSubRows = (item) => {
    return (item?.serials?.length ?? 0) > 0 || !!item?.commentRow;
};

/**
 * Creates a product row given the item and its index.
 * @param item
 * @param index
 * @param length
 * @param showPrices
 * @return {JSX.Element}
 */
const createProductRow = (item, index, length, showPrices) => {
    const hasSubRows = itemHasPrintSubRows(item);
    return (
        <tr key={item.partNo}
            className={classnames('table-body side-borders',
                {' no-bottom-border': hasSubRows && index !== length - 1})}
        >
            <td colSpan={showPrices ? 12 : 15}
                className={classnames('part-number-fluid text-left font-weight-semi-bold text-s no-right-border',
                    {'no-padding-bottom': hasSubRows})}
                style={{paddingBottom: hasSubRows ? '0px !important' : '3px !important'}}
            >
                {item?.partNo ?? ''}
            </td>
            <td colSpan={showPrices ? 24 : 30}
                className={classnames('text-left font-weight-semi-bold text-s no-left-border',
                    {'no-padding-bottom': hasSubRows})}
                style={{paddingBottom: hasSubRows ? '0px !important' : '3px !important'}}
            >
                {item?.description ?? ''}
            </td>
            <td colSpan={showPrices ? 4 : 5}
                className={'text-center font-weight-semi-bold text-s no-right-border'}>
                {item?.orderQty === '0' ? '' : item?.orderQty ?? ''}
            </td>
            <td colSpan={showPrices ? 5 : 6}
                className={'text-center font-weight-semi-bold text-s no-right-border no-left-border'}>
                {item?.committedQty === '0' ? '' : item?.committedQty ?? ''}
            </td>
            <td colSpan={showPrices ? 3 : 4}
                className={'text-right font-weight-semi-bold text-s no-left-border'}>
                {item?.backorderQty === '0' ? '' : item?.backorderQty ?? ''}
            </td>
            {
                showPrices &&
                <>
                    <td colSpan={4} className={'text-left font-weight-semi-bold text-s no-right-border'}>
                        {formatMoney(item?.unitPriceBeforeDiscount)?.replace('$', '') ?? ''}
                    </td>
                    <th colSpan={3}
                        className={'text-center font-weight-semi-bold text-s text-s no-left-border no-right-border'}>
                        {`${item?.discountPct ?? '0'}%`}
                    </th>
                    <td colSpan={5} className={'text-right font-weight-semi-bold text-s no-left-border'}>
                        {formatMoney(parseFloat(item?.unitPrice) * parseFloat(item.committedQty))?.replace('$', '') ?? ''}
                    </td>
                </>
            }
        </tr>
    );
}

/**
 * Creates a serial row
 * @param first
 * @param second
 * @param props
 * @param key
 * @param showPrices
 * @return {JSX.Element}
 */
const createSerialNoRow = ({data: {first = "", second = ""} = {}, key, ...props}, showPrices) => {
    return (
        <tr key={key} {...props} className={'p-0 no-top-border no-bottom-border serial-number side-borders'}>
            <td colSpan={showPrices ? 12 : 15}
                className={'pl-3 text-left font-weight-semi-bold font-italic text-xs no-right-border'}>
                {first ? `S/N: ${first}` : ''}
            </td>
            <td colSpan={showPrices ? 24 : 30}
                className={'pl-3 text-left font-weight-semi-bold font-italic text-xs no-left-border'}>
                {second ? `S/N: ${second}` : ''}
            </td>
            <td colSpan={showPrices ? 4 : 5} className={'no-right-border'}/>
            <td colSpan={showPrices ? 5 : 6} className={'no-right-border no-left-border'}/>
            <td colSpan={showPrices ? 3 : 4} className={'no-left-border'}/>
            {
                showPrices &&
                <>
                    <td colSpan={4} className={'no-right-border'}/>
                    <td colSpan={3} className={'no-left-border no-right-border'}/>
                    <td colSpan={5} className={'no-left-border'}/>
                </>
            }
        </tr>
    );
}

/**
 * Creates a product comment row beneath serial number rows.
 * @param data
 * @param key
 * @param showPrices
 * @param props
 * @return {JSX.Element|null}
 */
const createProductCommentRow = ({data: {comment = ""} = {}, key, ...props}, showPrices) => {
    if (!EnvService.DEV_showOrderProductComments || !comment?.trim()) {
        return null;
    }

    return (
        <tr key={key} {...props} className={'p-0 no-top-border no-bottom-border product-comment side-borders'}>
            <td colSpan={showPrices ? 36 : 45}
                className={'pl-3 text-left font-weight-semi-bold font-italic text-xs no-right-border no-left-border'}>
                {`Comment: ${comment}`}
            </td>
            <td colSpan={showPrices ? 4 : 5} className={'no-right-border'}/>
            <td colSpan={showPrices ? 5 : 6} className={'no-right-border no-left-border'}/>
            <td colSpan={showPrices ? 3 : 4} className={'no-left-border'}/>
            {
                showPrices &&
                <>
                    <td colSpan={4} className={'no-right-border'}/>
                    <td colSpan={3} className={'no-left-border no-right-border'}/>
                    <td colSpan={5} className={'no-left-border'}/>
                </>
            }
        </tr>
    );
}


// noinspection JSCommentMatchesSignature
/**
 * Creates a Table Rooter Row.
 * @param {boolean} fullWidth whether full width
 * @return {JSX.Element}
 */
const createTableFooterRow = ({
                                  fullWidth = false,
                                  className = '',
                                  data: {
                                      left: {leftData = '', leftProps = {}, html = false} = {},
                                      center: {centerData = '', centerProps = {}} = {},
                                      right: {rightData = '', rightProps = {}} = {},
                                  } = {},
                                  key,
                                  showPrices,
                                  ...props
                              }) => {
    if (fullWidth) {
        return (
            <tr key={key} {...props} className={classnames('side-borders', className)}>
                <td colSpan={showPrices ? 36 : 60}{...leftProps} className={classnames('text-left font-weight-semi-bold text-s', leftProps.className)}>
                    {
                        html ? <div dangerouslySetInnerHTML={{__html: leftData ?? ""}}/> : leftData ?? ""
                    }
                </td>
                {
                    showPrices &&
                    <td colSpan={24}
                        {...rightProps}
                        className={classnames('text-left font-weight-semi-bold text-s', rightProps.className)}>
                        {rightData ?? ''}
                    </td>
                }
            </tr>
        );
    }
    return (
        <tr key={key} {...props} className={classnames('side-borders', className)}>
            <td colSpan={8}
                {...leftProps}
                className={classnames('text-left no-right-border font-weight-semi-bold text-s', leftProps.className)}>
                {
                    html ? <div dangerouslySetInnerHTML={{__html: leftData ?? ""}}/> : leftData ?? ""
                }
            </td>
            <td colSpan={9}
                {...centerProps}
                className={classnames('text-right no-right-border no-left-border font-weight-semi-bold text-sm', centerProps.className)}>
                {centerData ?? ''}
            </td>
            <td colSpan={7}
                {...rightProps}
                className={classnames('text-right no-right-border no-left-border font-weight-semi-bold text-sm', rightProps.className)}>
                {rightData ?? ''}
            </td>
        </tr>
    );
}

/**
 * Creates an order note row.
 * @param note
 * @param index
 * @param length
 * @param props
 * @return {JSX.Element}
 */
const createNoteRow = (note, index, length, props = {}) => {
    const {body, subject} = note ?? {}
    const {innerProps, ...restProps} = props ?? {};
    return (
        <tr {...restProps} key={index} className={classnames('table-body side-borders',
            {' no-bottom-border': index !== length - 1})}>
            <td colSpan={60}
                {...innerProps ?? {}}
                className={classnames('text-left pt-3 font-weight-semi-bold text-xs', innerProps?.className)}>
                {subject && <p className={'note-subject'}>{subject}</p>}
                <p className={'note-body'}>{body}</p>
            </td>
        </tr>
    );
}


const PrintInvoiceTemplate = ({
                                  invoice,
                                  partner,
                                  credit,
                                  rules,
                                  showTransactions,
                                  showAddresses,
                                  showPrices,
                                  showPaymentTerms,
                                  showPromotion,
                                  promotionText,
                              }) => {
    const notes = useMemo(() => invoice?.printableNotes?.sort((a, b) => dateComparator(a.createdDate, b.createdDate)) ?? [], [invoice]);

    const transactions = useMemo(() => {
        const transactions = invoice?.appliedTransactions?.filter(e => Number(e.reference) !== invoice?.id) ?? [];
        const taxesLength = invoice?.taxes?.length;
        // 22 for max - 5 for sub/dis/shipping/tot/bal - taxLengths
        return transactions.slice(0, Math.max(22 - 5 - taxesLength, 0))
    }, [invoice?.appliedTransactions, invoice?.id, invoice?.taxes?.length]);

    // 5 for sub/dis/shipping/tot/bal + 1 extra = 6
    const rowSpanOfRules = useMemo(() => 6 + (invoice?.taxes?.length ?? 0) + (showTransactions ? transactions.length ?? 0 : 0) + (!showTransactions ? -1 : 0), [invoice, transactions, showTransactions]);
    const items = useMemo(() => [...(invoice?.items ?? [])].map((item) => {
        const pairs = [];
        item?.serials?.forEach(e => {
            if (!pairs.length) {
                return pairs.push([e]);
            }
            if (pairs[pairs.length - 1]?.length === 1) {
                return pairs[pairs.length - 1].push(e);
            }
            pairs.push([e]);
        });
        const serials = pairs?.map(([first, second]) => createSerialNoRow({
            data: {first: first?.serialNumber ?? "", second: second?.serialNumber ?? ""},
            key: createUUId(true),
        }, showPrices)) ?? [];
        const commentRow = createProductCommentRow({
            data: {comment: item?.comment ?? ""},
            key: createUUId(true),
        }, showPrices);
        return {...item, serials, commentRow};
    }), [invoice, showPrices]);

    const {billingAddress, shippingAddress} = useMemo(() => ({
        billingAddress: invoice?.billingAddress,
        shippingAddress: invoice?.shippingAddress
    }), [invoice?.shippingAddress, invoice?.billingAddress]);

    const subTotal = useMemo(() => invoice?.subTotal ?? 0, [invoice?.subTotal]);

    const discount = useMemo(() => {
        const discount = (invoice?.totalItemsDiscountAmount ?? 0);
        if (discount > 0) {
            return discount;
        }
        return 0;
    }, [invoice?.totalItemsDiscountAmount]);

    const shippingFee = useMemo(() => (invoice?.freightFee ?? 0), [invoice?.freightFee]);
    const total = useMemo(() => invoice?.total ?? 0, [invoice?.total]);
    const balance = useMemo(() => invoice?.balance ?? 0, [invoice?.balance]);


    /**
     * Creates the parts with their serial numbers for the invoices table
     * @return {*}
     */
    const createProductTableBody = () => {
        return items.map((item, index) => {
            return [
                createProductRow(item, index, items.length, showPrices),
                ...item.serials,
                ...(item.commentRow ? [item.commentRow] : []),
            ]
        })
    }

    /**
     * Creates the taxes with their applied rates and exempt numbers
     * @return {*}
     */
    const createProductTableTaxes = () => {
        return invoice?.taxes?.map((tax, index) => (
            createTableFooterRow({
                className: 'no-bottom-border no-top-border',
                key: index,
                data: {
                    left: {leftData: !showPrices ? "" : tax.exemptNo ? `${tax.name} # ${tax.exemptNo}` : ''},
                    center: {centerData: !showPrices ? "" : tax.name ?? ''},
                    right: {rightData: !showPrices ? "" : formatMoney(tax.total).replace('$', '')},
                },
                showPrices,
            })
        ))
    }

    /**
     * Creates the credits with their refund values for the table
     * @return {*}
     */
    const createProductTableCredits = () => {
        if (!showTransactions) {
            return <></>;
        }
        return transactions?.map((transaction, index) => (
            createTableFooterRow({
                key: index,
                className: 'no-bottom-border no-top-border',
                data: {
                    left: {
                        leftData: !showPrices ? "" : transaction?.transactionTypeName === AccountingTransactionTypes.payment
                            ? `Auth code: ${transaction?.authorizationCode ?? ''}`
                            : ''
                    },
                    center: {centerData: !showPrices ? "" : `${transaction?.transactionTypeDescription ?? '--'}:`},
                    right: {rightData: !showPrices ? "" : `-${formatMoney(transaction?.amount).replace('$', '')}`}
                },
                showPrices,
            })
        ))
    }

    /**
     * Creates the notes' table body.
     * @return {*}
     */
    const createNoteTableBody = () => {
        return notes?.map((note, index) => (
            createNoteRow(note, index, notes.length)
        ))
    }

    return (
        <>
            <div id={ElementIds.printInvoiceView.page} className={'print-invoice-template'}>
                <table id={ElementIds.printInvoiceView.table} width={'100%'} className={'table'}>
                    <colgroup>
                        {
                            Array(60).fill(1).map((e, index) => (
                                <col key={index} width="1.6666666666667%"/>
                            ))
                        }
                    </colgroup>
                    <tbody id={ElementIds.printInvoiceView.pageHeader}>
                    <tr className={''}>
                        <td colSpan={20} className={'no-border pr-4'}>
                            <img
                                src={Utils.encodeFileUrl(partner.logo)}
                                style={{objectFit: 'contain', height: 'fit-content', maxHeight: 120}}
                                className={'w-100'}
                                alt="logo"
                            />
                        </td>
                        <td colSpan={20} className={'no-border'}>
                            <p className={'text-xxl font-weight-bold black'}>
                                {partner.name}
                            </p>
                            {/*PARTNER ADDRESS AND PHONE */}
                            <p className={'text-md font-weight-bold black' +
                                ' line-height-2'}>
                                {partner.fullAddress}
                            </p>
                            <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                                {partner.city} {partner.province} {partner.postalCode}
                            </p>
                            <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                                Ph. <span>{partner.phone}</span>
                            </p>
                            <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                                Fx. <span>{partner.fax}</span>
                            </p>
                            <p className={'text-md font-weight-bold black mr-1 line-height-2'}>
                                W: <span> {Utils.formatLinkUrlDisplay(partner.website) ?? ''}</span>
                            </p>
                        </td>
                        <td colSpan={20} className={'no-border'}>
                            {/* Invoice Details */}
                            <p className={'invoice-text black font-weight-bold mb-2'}>
                                {credit ? "Credit" : "Invoice"}
                            </p>
                            <p className={'font-weight-bold text-lg black mb-1'}>
                                {invoice?.invoiceNo ?? ''}
                            </p>
                            <p className={'text-md'}>
                                    <span className={'font-weight-bold'} style={{paddingRight: 100}}>
                                        Date
                                    </span>
                                <span
                                    className={'font-weight-semi-bold text-sm'}>
                                        {DateUtils.serverToLocal(invoice?.invoiceDate).format(DateUtils.dateFormatAlternative)}
                                    </span>
                            </p>
                            <p className={'text-md'}>
                                    <span className={'font-weight-bold'} style={{paddingRight: 63}}>
                                        Customer
                                    </span>
                                <span
                                    className={'font-weight-semi-bold text-sm'}>
                                        {invoice?.customer?.customerNo ?? ''}
                                    </span>
                            </p>
                        </td>
                    </tr>
                    <tr className={''}>
                        <td colSpan={45} className={'no-border'} height={'50px'}/>
                        <td colSpan={15} className={'no-border'} height={'50px'}>
                            <p className={'page-identifier'}>
                                Page 0 of 0
                            </p>
                        </td>
                    </tr>
                    <tr className={''}>
                        <td colSpan={30} className={'no-border pt-4'}>
                            {
                                showAddresses &&
                                <>
                                    {/*BILL-TO ADDRESS*/}
                                    <p className={'text-md font-weight-bold mr-2'}>
                                        <span style={{paddingRight: 30}}>Bill To:</span>
                                        <span>{invoice?.customer?.name ?? ''}</span>
                                    </p>
                                    <p className={'text-md font-weight-semi-bold'}>
                                        <span style={{paddingLeft: 82}}>{billingAddress?.fullAddress ?? ''}</span>
                                    </p>
                                    <p className={'text-md font-weight-semi-bold pb-4'}>
                                            <span style={{paddingLeft: 82}}>
                                                {billingAddress?.city ?? ''}
                                            </span>
                                        <span className={'ml-2'}>
                                                {billingAddress?.province?.code ?? ''}
                                            </span>
                                        <span className={'ml-2'}>
                                                {billingAddress?.postalCode ?? ''}
                                            </span>
                                    </p>
                                    <p className={'text-md font-weight-semi-bold black'}>
                                            <span style={{paddingLeft: 82}}>
                                                {
                                                    billingAddress?.phone &&
                                                    `Ph. ${formatPhoneNumber(billingAddress?.phone)}`
                                                }
                                            </span>
                                    </p>
                                </>
                            }
                        </td>
                        <td colSpan={30} className={'no-border  pt-4'}>
                            {
                                showAddresses &&
                                <>
                                    <p className={'text-md font-weight-bold mr-2'}>
                                        <span style={{paddingRight: 30}}>Ship To:</span>
                                        <span>{invoice?.customer?.name ?? ''}</span>
                                    </p>
                                    <p className={'text-md font-weight-semi-bold'}>
                                        <span style={{paddingLeft: 92}}>{shippingAddress?.fullAddress ?? ''}</span>
                                    </p>
                                    <p className={'text-md font-weight-semi-bold pb-4'}>
                                            <span style={{paddingLeft: 92}}>
                                                {shippingAddress?.city ?? ''}
                                            </span>
                                        <span className={'ml-2'}>
                                                {shippingAddress?.province?.code ?? ''}
                                            </span>
                                        <span className={'ml-2'}>
                                                {shippingAddress?.postalCode ?? ''}
                                            </span>
                                    </p>
                                    <p className={'text-md font-weight-semi-bold black'}>
                                            <span style={{paddingLeft: 92}}>
                                                {
                                                    shippingAddress?.phone &&
                                                    `Ph. ${formatPhoneNumber(shippingAddress?.phone)}`
                                                }
                                            </span>
                                    </p>
                                </>
                            }
                        </td>
                    </tr>
                    <tr className={''}>
                        <td className={'no-border'} colSpan={60} height={'100px'}/>
                    </tr>
                    </tbody>
                    <tbody id={ElementIds.printInvoiceView.productsTable.header}>
                    {/*FIRST ROW OF TABLE*/}
                    <tr className={'side-top-borders'}>
                        <th colSpan={12} className={'text-center text-md'}>
                            PO Number
                        </th>
                        <th colSpan={12} className={'text-center text-md'}>
                            F.O.B.
                        </th>
                        <th colSpan={18} className={'text-center text-md'}>
                            Account manager
                        </th>
                        <th colSpan={9} className={'text-center text-md'}>
                            Order Date
                        </th>
                        <th colSpan={9} className={'text-center text-md'}>
                            Order Number
                        </th>
                    </tr>
                    <tr className={'side-borders'}>
                        <td colSpan={12} className={'text-center text-sm'}>
                            {invoice?.customerPO ?? '--'}
                        </td>
                        <td colSpan={12} className={'text-center text-sm'}>
                            {invoice?.fob ?? '--'}
                        </td>
                        <td colSpan={18} className={'text-center text-sm'}>
                            {invoice?.accountManagerNo ?? '--'}
                        </td>
                        <td colSpan={9} className={'text-center text-sm'}>
                            {invoice?.orderDate ? DateUtils.serverToLocal(invoice?.orderDate).format(DateUtils.dateFormatAlternative) : '--'}
                        </td>
                        <td colSpan={9} className={'text-center text-sm'}>
                            {invoice?.orderNo ?? '--'}
                        </td>
                    </tr>
                    {/*SECOND ROW OF TABLE*/}
                    <tr className={'side-borders'}>
                        <th colSpan={showPaymentTerms ? 12 : 18} className={'text-center text-md'}>
                            Ship Via
                        </th>
                        <th colSpan={showPaymentTerms ? 12 : 15} className={'text-center text-md'}>
                            Tracking No
                        </th>
                        {
                            showPaymentTerms &&
                            <th colSpan={18} className={'text-center text-md'}>
                                Payment Terms
                            </th>
                        }
                        <th colSpan={showPaymentTerms ? 18 : 27} className={'text-center text-md'}>
                            Reference
                        </th>
                    </tr>
                    <tr className={'side-borders'}>
                        <td colSpan={showPaymentTerms ? 12 : 18} className={'text-center text-sm'}>
                            {invoice?.shippingCarrier ?? '--'}
                        </td>
                        <td colSpan={showPaymentTerms ? 12 : 15} className={'text-center text-sm'}>
                            {invoice?.trackingNo ?? '--'}
                        </td>
                        {
                            showPaymentTerms &&
                            <td colSpan={18} className={'text-center text-sm'}>
                                {invoice?.paymentTermsCode ?? invoice?.termsCode ?? '--'}
                            </td>
                        }
                        <td colSpan={showPaymentTerms ? 18 : 27} className={'text-center text-sm'}>
                            {invoice?.invoiceNo ?? '--'}
                        </td>
                    </tr>
                    <tr className={'side-borders'}>
                        <th colSpan={60}/>
                    </tr>
                    {/*THIRD ROW OF TABLE*/}
                    <tr className={'side-borders'}>
                        <th colSpan={showPrices ? 12 : 15}
                            className={'text-left text-md align-bottom no-right-border'}>
                            Part Number
                        </th>
                        <th colSpan={showPrices ? 24 : 30}
                            className={'text-left text-md align-bottom no-left-border'}>
                            Description
                        </th>
                        <th colSpan={showPrices ? 4 : 5}
                            className={'text-left align-bottom text-md no-right-border'}>
                            Ordered
                        </th>
                        <th colSpan={showPrices ? 5 : 6}
                            className={'text-center text-md no-right-border align-bottom no-left-border'}>
                            Shipped
                        </th>
                        <th colSpan={showPrices ? 3 : 4}
                            className={'text-right text-md align-bottom no-left-border '}>
                            B/O
                        </th>
                        {
                            showPrices &&
                            <>
                                <th colSpan={4}
                                    className={'text-left text-md align-bottom no-right-border'}>
                                    Unit Price
                                </th>
                                <th colSpan={3}
                                    className={'text-left text-md align-bottom no-left-border no-right-border'}>
                                    Discount
                                </th>
                                <th colSpan={5}
                                    className={'text-right text-md align-bottom no-left-border'}>
                                    Extended Price
                                </th>
                            </>
                        }
                    </tr>
                    </tbody>
                    <tbody id={ElementIds.printInvoiceView.productsTable.body} className={'table-border'}>
                    {
                        createProductTableBody()
                    }
                    {/*BOTTOM ROW OF TABLE BODY*/}
                    <tr id={ElementIds.printInvoiceView.productsTable.bodyFiller} className={classnames('no-bottom-border' +
                        ' no-top-border side-borders')}>
                        <td colSpan={showPrices ? 36 : 45} className={classnames('py-2-5 text-left font-weight-semi-bold text-md')}/>
                        <td colSpan={showPrices ? 12 : 15} className={classnames('py-2-5 text-left font-weight-semi-bold text-md')}/>
                        {
                            showPrices &&
                            <td colSpan={12} className={classnames('py-2-5 text-left font-weight-semi-bold text-md')}/>
                        }
                    </tr>
                    </tbody>
                    <tbody id={ElementIds.printInvoiceView.productsTable.footerContinue} className={'table-footer-border'}>
                    {createTableFooterRow({
                        className: 'no-bottom-border no-top-border',
                        fullWidth: true,
                        height: '120px',
                        data: {
                            ...(showPrices && {
                                right: {
                                    rightData: "Continued...",
                                    rightProps: {className: 'py-2-5 text-lg font-weight-bold'}
                                },
                            }),
                            ...(!showPrices && {
                                left: {
                                    leftData: "Continued...",
                                    leftProps: {className: 'py-2-5 text-lg font-weight-bold'}
                                },
                            }),
                        },
                        showPrices,
                    })}
                    </tbody>
                    <tbody id={ElementIds.printInvoiceView.productsTable.footer} className={'table-footer-border'}>
                    {/*RULES*/}
                    {createTableFooterRow({
                        className: 'no-bottom-border no-top-border',
                        fullWidth: true,
                        data: {
                            left: {
                                leftData: `<p>${rules.replaceAll('\n', '<br/>')}</p>`,
                                leftProps: {rowSpan: rowSpanOfRules, className: 'text-xs'},
                                html: true,
                            },
                        },
                        showPrices
                    })}
                    {/*SUB_TOTAL*/}
                    {createTableFooterRow({
                        className: 'no-bottom-border no-top-border',
                        data: {
                            left: {leftProps: {className: 'pt-3'}},
                            center: {
                                centerData: !showPrices ? "" : "Sub Total",
                                centerProps: {className: 'pt-3'}
                            },
                            right: {
                                rightData: !showPrices ? "" : formatMoney(subTotal).replace('$', ''),
                                rightProps: {className: 'pt-3'}
                            },
                        },
                        showPrices
                    })}
                    {/*DISCOUNT*/}
                    {createTableFooterRow({
                        className: 'no-bottom-border no-top-border',
                        data: {
                            center: {centerData: !showPrices ? "" : "Discount"},
                            right: {rightData: !showPrices ? "" : `-${formatMoney(discount).replace('$', '')}`},
                        },
                        showPrices
                    })}
                    {/*SHIPPING FEE*/}
                    {createTableFooterRow({
                        className: 'no-bottom-border no-top-border',
                        data: {
                            center: {centerData: !showPrices ? "" : "Shipping Fee"},
                            right: {rightData: !showPrices ? "" : `${formatMoney(shippingFee).replace('$', '')}`},
                        },
                        showPrices
                    })}
                    {/*TAXES*/}
                    {createProductTableTaxes()}
                    {/*TOTAL*/}
                    {createTableFooterRow({
                        className: 'no-bottom-border no-top-border',
                        data: {
                            center: {
                                centerData: !showPrices ? "" : "Total",
                                centerProps: {className: 'font-weight-bold text-lg'}
                            },
                            right: {
                                rightData: !showPrices ? "" : formatMoney(total).replace('$', ''),
                                rightProps: {className: 'font-weight-bold text-lg'}
                            },
                        },
                        showPrices
                    })}
                    {/*CREDITS*/}
                    {createProductTableCredits()}
                    {/*BALANCE*/}
                    {
                        showTransactions &&
                        createTableFooterRow({
                            className: 'no-bottom-border no-top-border',
                            data: {
                                center: {
                                    centerData: !showPrices ? "" : "Total Due",
                                    centerProps: {className: 'font-weight-bold text-lg'}
                                },
                                right: {
                                    rightData: !showPrices ? "" : formatMoney(balance).replace('$', ''),
                                    rightProps: {className: 'font-weight-bold text-lg'}
                                },
                            },
                            showPrices
                        })
                    }

                    {/*BOTTOM ROW OF TABLE FOOTER*/}
                    {createTableFooterRow({
                        className: 'no-bottom-border no-top-border',
                        fullWidth: true,
                        data: {
                            left: {leftProps: {className: 'py-1'}},
                            right: {rightProps: {className: 'py-1'}},
                        },
                        showPrices
                    })}
                    {/*SIGNATURE*/}
                    {createTableFooterRow({
                        className: 'no-bottom-border no-top-border',
                        fullWidth: true,
                        data: {
                            left: {leftData: "Signature:", leftProps: {className: 'py-1 pl-2 text-md'}},
                            right: {rightProps: {className: 'py-1'}},
                        },
                        showPrices,
                    })}
                    </tbody>
                    <tbody id={ElementIds.printInvoiceView.tablesDistance} className={'border-none'}>
                    {
                        showPromotion &&
                        <tr>
                            <td colSpan={60} style={{paddingTop: '50px', paddingBottom: '50px'}}
                                className={'no-border text-xxl font-weight-bold'}>
                                {promotionText ?? ""}
                            </td>
                        </tr>
                    }
                    </tbody>

                    {/*NOTES TABLE*/}
                    <tbody id={ElementIds.printInvoiceView.notesTable.header}>
                    <tr className={'side-top-borders border-0'}>
                        <th colSpan={60} className={'text-start text-md border-0'}>
                            Note Description
                        </th>
                    </tr>
                    </tbody>
                    <tbody id={ElementIds.printInvoiceView.notesTable.body} className={'table-border notes-table'}>
                    {createNoteTableBody()}
                    </tbody>
                    <tbody id={ElementIds.printInvoiceView.notesTable.footerContinue} className={'table-footer-border'}>
                    {createNoteRow(
                        'Continued...',
                        createUUId(),
                        0,
                        {
                            className: "no-border",
                            innerProps: {
                                className: 'text-right text-md pr-3 font-weight-bold'
                            }
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default PrintInvoiceTemplate;


